"use client";

import { ShopProduct } from "@/lib/types/shop";
import { FashionShopProductCard } from "./FashionShopProductCard";
import { ElectronicsShopProductCard } from "./ElectronicsShopProductCard";
import { FoodShopProductCard } from "./FoodShopProductCard";
import { isFoodProduct } from "@/lib/config/food";
import { MotorsShopProductCard } from "./MotorsShopProductCard";
import { isVehicleProduct } from "@/lib/config/motors";
import { PropertyShopProductCard } from "./PropertyShopProductCard";
import { isPropertyListing } from "@/lib/config/property";
import { shopCardTemplate } from "@/lib/config/industryForms";

interface ShopProductCardProps {
  product: ShopProduct;
  className?: string;
  industry?: "fashion" | "electronics"; // Override industry from parent
  productUrlPrefix?: string; // e.g., "/fashion" for marketplace
}

/**
 * Unified Shop Product Card
 * 
 * Automatically switches between Fashion and Electronics card designs
 * based on the product's industry or the industry prop.
 * 
 * - Fashion: Uses image hover effects, minimal design (mutoniaccessorieshub style)
 * - Electronics: Uses warranty badges, condition indicators (kuttos style)
 */
export function ShopProductCard({ product, className, industry, productUrlPrefix = "" }: ShopProductCardProps) {
  // The PRODUCT decides, the prop is only a fallback. It used to be the
  // other way round, and every parent passed the BRAND's industry — so a
  // fashion shop's power bank rendered as a fashion card.
  // Menu items (Food & Beverage) get the menu card regardless of the host.
  if (isFoodProduct(product)) {
    return <FoodShopProductCard product={product as any} className={className} productUrlPrefix={productUrlPrefix} />;
  }
  // A car is never a shelf item: landscape photo, year-first title, spec strip.
  if (isVehicleProduct(product)) {
    return <MotorsShopProductCard product={product as any} className={className} productUrlPrefix={productUrlPrefix} />;
  }
  // Nor is a house: price first, a spec strip of beds/baths/size/parking,
  // and the landmark that actually places it.
  if (isPropertyListing(product)) {
    return <PropertyShopProductCard product={product as any} className={className} productUrlPrefix={productUrlPrefix} />;
  }

  // Its industry's template draws the card (lib/config/industryForms.ts):
  // fashion and the industries on its form (Events & Decor, Sports &
  // Fitness) the fashion card, everything else the electronics one. A
  // fashionAttributes bucket used to decide on its own, and every shop row
  // carried an empty one, so every phone drew the fashion card (Nov 2026).
  if (shopCardTemplate(product, industry) === "fashion") {
    return <FashionShopProductCard product={product} className={className} productUrlPrefix={productUrlPrefix} />;
  }

  // Use Electronics card for electronics industry (default)
  return <ElectronicsShopProductCard product={product} className={className} />;
}

